import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { NotifierService } from 'angular-notifier';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { ConfirmboxComponent } from 'src/app/core/confirmbox/confirmbox.component';
import { HeaderService } from 'src/app/core/header/header.service';
import { Pagination } from 'src/app/core/pagination/pagination';
import { ConfigService } from 'src/app/core/services/config.service';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { SortingService } from 'src/app/core/services/sorting.service';
import { StatusHandlerService } from 'src/app/core/services/status-handler.service';
import { XLSValidator } from 'src/app/core/validations/xls-validator';
import { PermissionManagerService } from 'src/app/Permissions/permission-manager.service';
import { UserService } from 'src/app/user/user.service';
import { SyncRcmMasterDataService } from '../sync-rcm-master-data.service';

@Component({
  selector: 'app-sync-rcm-master-data',
  templateUrl: './sync-rcm-master-data.component.html',
  styleUrls: ['./sync-rcm-master-data.component.scss']
})
export class SyncRcmMasterDataComponent implements OnInit {

  public pagination: Pagination | any;
  public masterTablesOptionsList: any = [];
  public showLoader: boolean = false;
  public errorMessage: any = '';
  private readonly notifier: NotifierService;
  selectedTableIds: number[] = [];
  public colSpan: number = 2;
  public filteredData: any = [];
  public pagedSyncMasterData: any[] = [];
  
  // Pagination properties
  currentPage: number = 1;
  totalPages: number = 0;
  totalRecordsForPagination: number = 0;

  public filters = {
    TableName: '',
    LastUpdatedOn: '',
  };

  selectedFilter: { [key: string]: number } = {
    TableName: 4,
    LastUpdatedOn: 4
  };

  public columnDropdownFilter = [
    {
      id: 1, name: 'Is equal to', activeTableName: false, activeLastUpdatedOn: false
    },
    {
      id: 2, name: 'Is not equal to', activeTableName: false, activeLastUpdatedOn: false
    },
    {
      id: 3, name: 'Starts with', activeTableName: false, activeLastUpdatedOn: false
    },
    {
      id: 4, name: 'Contains', activeTableName: true, activeLastUpdatedOn: true
    },
    {
      id: 5, name: 'Does not contain', activeTableName: false, activeLastUpdatedOn: false
    },
    {
      id: 6, name: 'Ends with', activeTableName: false, activeLastUpdatedOn: false
    },
    {
      id: 7, name: 'Is null', activeTableName: false, activeLastUpdatedOn: false
    },
    {
      id: 8, name: 'Is not null', activeTableName: false, activeLastUpdatedOn: false
    }
  ];

  constructor(
    private headerService: HeaderService,
    private syncRcmMasterDataService: SyncRcmMasterDataService,
    private errorHandler: ErrorHandlerService,
    private notifierService: NotifierService,
    private sortingService: SortingService
  ) {
    this.notifier = notifierService;
  }

  public SyncMasterData: any = [];
  
  ngOnInit(): void {
    this.pagination = new Pagination("TableNames", false, 1);
    this.pagination.pageLength = 50;
    this.GetMasterTablesOptions();
    this.loadSyncMasterData();
    this.setDefaultFilterActiveStates();
  }

  loadSyncMasterData() {
    this.showLoader = true;

    this.syncRcmMasterDataService.getAllData().subscribe({
      next: (res: any) => {
        this.SyncMasterData = res.Data;
        this.applyFilters();
        this.showLoader = false;   // stop loader only on success
      },
      error: (err) => {

        this.errorMessage = "Failed to load Sync Master Data. Please try again later.";
        this.showLoader = false;   
      }
    });
  }

  GetMasterTablesOptions() {
    this.masterTablesOptionsList = [];
    this.syncRcmMasterDataService.GetMasterTablesOptions().subscribe({
      next: (data: any) => {
        this.masterTablesOptionsList = data.Data;
        this.selectedTableIds = this.masterTablesOptionsList.map((item: { MasterTableId: any; }) => item.MasterTableId);
      }, error: (error: any) => {
        this.errorMessage = this.errorHandler.GetErrorMessage("RCM sync:", error.status);
        this.notifier.notify('error', this.errorMessage);
      }
    });
  }

  // Filter matching function
  matchFilter(itemValue: any, filterValue: string, filterType: number): boolean {
    // If no filter is applied, pass everything
    if (filterValue === null || filterValue === '') return true;
    if (itemValue === null || itemValue === undefined) return false;

    const item = itemValue.toString().toLowerCase();
    const filter = filterValue ? filterValue.toString().toLowerCase() : '';

    // Handle text input filter based on filterType
    if (filterValue && filterValue !== '') {
      switch (filterType) {
        case 1: // Is equal to
          return item === filter;
        case 2: // Is not equal to
          return item !== filter;
        case 3: // Starts with
          return item.startsWith(filter);
        case 4: // Contains
          return item.includes(filter);
        case 5: // Does not contain
          return !item.includes(filter);
        case 6: // Ends with
          return item.endsWith(filter);
        case 7: // Is null
          return itemValue === null || itemValue === '';
        case 8: // Is not null
          return itemValue !== null && itemValue !== '';
        default:
          return true;
      }
    }

    return true;
  }

  setDefaultFilterActiveStates() {
    // Reset all active states
    this.columnDropdownFilter.forEach(item => {
      item.activeTableName = false;
      item.activeLastUpdatedOn = false;
    });

    // Set "Contains" as active for both columns by default
    const containsFilter = this.columnDropdownFilter.find(item => item.id === 4);
    if (containsFilter) {
      containsFilter.activeTableName = true;
      containsFilter.activeLastUpdatedOn = true;
    }
  }

  // Update the applyFilterOption method
  applyFilterOption(column: string, index: number) {
    // Only update the filter type, don't trigger sorting
    this.selectedFilter[column] = this.columnDropdownFilter[index].id;

    // Reset active states for all filters for this specific column
    this.columnDropdownFilter.forEach(item => {
      if (column === 'TableName') {
        item.activeTableName = false;
      } else if (column === 'LastUpdatedOn') {
        item.activeLastUpdatedOn = false;
      }
    });

    // Set active state for the current filter
    if (column === 'TableName') {
      this.columnDropdownFilter[index].activeTableName = true;
    } else if (column === 'LastUpdatedOn') {
      this.columnDropdownFilter[index].activeLastUpdatedOn = true;
    }

    // Re-apply filters with new filter type but maintain current sort
    this.applyFiltersWithoutSorting();
  }

  applyFilters() {
    this.applyFiltersWithoutSorting();
    // After filtering, apply the current sort
    this.applyCurrentSort();
  }

  applyFiltersWithoutSorting() {
    this.filteredData = this.SyncMasterData.filter((item: any) => {
      const tableNameMatch = this.matchFilter(
        item.TableNames, 
        this.filters.TableName, 
        this.selectedFilter['TableName']
      );
      
      const lastUpdatedMatch = this.matchFilter(
        this.formatDate(item.LastUpdatedDate), 
        this.filters.LastUpdatedOn, 
        this.selectedFilter['LastUpdatedOn']
      );

      return tableNameMatch && lastUpdatedMatch;
    });

    this.totalRecordsForPagination = this.filteredData.length;
    this.calculatePagination();
    this.setPage(1);
  }

  applyCurrentSort() {
    if (this.pagination.columnName) {
      this.performSort(this.pagination.columnName, this.pagination.ascending);
    }
  }

  sortData(column: string) {
    if (this.pagination.columnName === column) {
      this.pagination.ascending = !this.pagination.ascending;
    } else {
      this.pagination.columnName = column;
      this.pagination.ascending = true;
    }

    this.performSort(column, this.pagination.ascending);
    this.setPage(this.currentPage);
  }

  performSort(column: string, ascending: boolean) {
    this.filteredData.sort((a: any, b: any) => {
      let comparison = 0;
      const valA = a[column] ?? '';
      const valB = b[column] ?? '';

      if (!isNaN(valA) && !isNaN(valB)) {
        comparison = Number(valA) - Number(valB);
      } else if (!isNaN(new Date(valA).getTime()) && !isNaN(new Date(valB).getTime())) {
        comparison = new Date(valA).getTime() - new Date(valB).getTime();
      } else {
        const strA = valA.toString().toUpperCase();
        const strB = valB.toString().toUpperCase();
        if (strA > strB) {
          comparison = 1;
        } else if (strA < strB) {
          comparison = -1;
        }
      }

      return ascending ? comparison : -comparison;
    });
  }

  isDateMatch(itemDate: Date | string, filterDate: string): boolean {
    if (!itemDate) return false;
    const itemDateString = this.formatDate(itemDate);
    return itemDateString.includes(filterDate);
  }

  formatDate(date: Date | string): string {
    if (!date) return '';

    const d = new Date(date);

    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    // Time
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert 24h → 12h format
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12

    const hoursStr = hours.toString().padStart(2, '0');

    // Pad month/day
    month = month.padStart(2, '0');
    day = day.padStart(2, '0');

    return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
  }

  isValidDate(dateString: string) {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  TableIds: any = ""
  submit() {
    this.showLoader = true;
    const idsString = this.selectedTableIds.join(',');
    this.TableIds = idsString;

    this.syncRcmMasterDataService.SubmitSelectedMasterTables(this.TableIds).subscribe({
      next: (data: any) => {
        this.showLoader = false;
        this.loadSyncMasterData();
        this.notifier.notify('success', 'Sync completed successfully');
      },
      error: (error: any) => {
        this.showLoader = false;
        this.errorMessage = this.errorHandler.GetErrorMessage("RCM sync:", error.status);
        this.notifier.notify('error', this.errorMessage);
      }
    });
  }

  // Pagination methods
  setPage(pageEvent: any) {
    let pageNum: number | null = null;

    if (typeof pageEvent === 'number') {
      pageNum = pageEvent;
    }

    if (pageNum === null && typeof pageEvent === 'object' && pageEvent !== null) {
      if (typeof pageEvent.page === 'number') pageNum = pageEvent.page;
      else if (typeof pageEvent.pageIndex === 'number') pageNum = pageEvent.pageIndex;
      else if (typeof pageEvent.currentPage === 'number') pageNum = pageEvent.currentPage;
    }

    if (pageNum === null) {
      pageNum = Number(pageEvent);
      if (isNaN(pageNum)) {
        console.warn('setPage: unable to determine page number from event:', pageEvent);
        return;
      }
    }

    const pageLength = Number(this.pagination?.pageLength ?? 10);
    const effectivePageLength = (!isNaN(pageLength) && pageLength > 0) ? pageLength : 10;

    this.currentPage = pageNum;

    const startIndex = (pageNum - 1) * effectivePageLength;
    const endIndex = startIndex + effectivePageLength;

    if (!this.filteredData || !Array.isArray(this.filteredData)) {
      this.pagedSyncMasterData = [];
      return;
    }

    this.pagedSyncMasterData = this.filteredData.slice(startIndex, endIndex);
  }

  GetDataByPageLength() {
    this.pagination.pageLength = Number(this.pagination.pageLength);
    this.calculatePagination();
    this.setPage(1);
  }

  calculatePagination() {
    const pageLength = Number(this.pagination.pageLength);
    this.totalPages = Math.ceil(this.totalRecordsForPagination / pageLength);
  }

  getDefaultSortColumn(): string {
    return 'TableNames';
  }

  GetSyncRcmDataToXLS() {
    this.showLoader = true;

    this.syncRcmMasterDataService.GetSyncRcmDataToXLS().subscribe({
      next: (blob: Blob) => {
        this.showLoader = false;

        // format file name
        const now = new Date();
        const formatted = `${now.getFullYear()}${(now.getMonth() + 1)
          .toString()
          .padStart(2, '0')}${now
            .getDate()
            .toString()
            .padStart(2, '0')}_${now
              .getHours()
              .toString()
              .padStart(2, '0')}${now
                .getMinutes()
                .toString()
                .padStart(2, '0')}${now
                  .getSeconds()
                  .toString()
                  .padStart(2, '0')}`;

        const fileName = `SyncRcmMasterData_${formatted}.xlsx`;

        // Create object URL
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);

        this.notifier.notify('success', 'Sync RCM Master Data exported successfully');
      },
      error: (error) => {
        this.showLoader = false;
        this.notifier.notify('error', 'Failed to export file');
      }
    });
  }

}